import { NAVIGATION_EVENTS } from 'src/lib/trackingEvents';

export type TFooterItem = {
  name: string;
  url: string;

  /** URLs are automatically prefixed with the current locale. This can be optionally overriden.  */
  locale?: string;

  trackingId?: string;
  id?: string;
};

export type TFooterSection = {
  header: string;
  items: TFooterItem[];
};

type TFooterSectionKey =
  | 'products'
  | 'useCases'
  | 'docs'
  | 'aboutUs'
  | 'resources'
  | 'forConsumers'
  | 'forInstitutions';

export type TFooterData = {
  copyrightName: string;
  eeaDisclaimer?: string[];
  /** @deprecated This is only used by the legacy `src/components/footer.jsx` component and is not applicable to new pages. It should be removed with the legacy footer. */
  hideCTAWhen: string[];
  sections: Partial<Record<TFooterSectionKey, TFooterSection>>;
};

export const footerData: TFooterData = {
  copyrightName: 'Plaid Inc.',
  hideCTAWhen: [
    '404',
    '500',
    'account-funding',
    'ach-start',
    'ACH-start',
    'assets',
    'auth',
    'balance',
    'banking',
    'business-brokerage-services',
    'business-finances',
    'careers',
    'consumer-payments',
    'contact-thanks',
    'contact',
    'customer-stories',
    'global',
    'how-it-works-for-consumers',
    'how-we-handle-data',
    'identity',
    'income',
    'integration',
    'investments',
    'job',
    'lending',
    'liabilities',
    'openings',
    'partners',
    'payment-initiation',
    'personal-finances',
    'plaid-exchange',
    'signup',
    'transactions',
    'use-cases',
    'wealth',
    'what-is-plaid',
    'why-is-plaid-involved',
    'trouble-connecting',
    '/', // The "/" represents homepage
  ],
  sections: {
    products: {
      header: 'Products',
      items: [
        {
          id: 'auth',
          name: 'Auth',
          url: '/products/auth/',
        },
        {
          id: 'identity',
          name: 'Identity',
          url: '/products/identity/',
        },
        {
          id: 'balance',
          name: 'Balance',
          url: '/products/balance/',
        },
        {
          id: 'signal',
          name: 'Signal',
          url: '/products/signal/',
        },
        {
          id: 'transfer',
          name: 'Transfer',
          url: '/products/transfer/',
        },
        {
          id: 'identity-verification',
          name: 'Identity Verification',
          url: '/products/identity-verification/',
        },
        {
          id: 'beacon',
          name: 'Beacon',
          url: '/products/beacon/',
        },
        {
          id: 'monitor',
          name: 'Monitor',
          url: '/products/monitor/',
        },
        {
          id: 'transactions',
          name: 'Transactions',
          url: '/products/transactions/',
        },
        {
          id: 'investments',
          name: 'Investments',
          url: '/products/investments/',
        },
        {
          id: 'liabilities',
          name: 'Liabilities',
          url: '/products/liabilities/',
        },
        {
          id: 'enrich',
          name: 'Enrich',
          url: '/products/enrich/',
        },
        {
          id: 'assets',
          name: 'Assets',
          url: '/products/assets/',
        },
        {
          id: 'income',
          name: 'Income',
          url: '/products/income/',
        },
        {
          id: 'plaid-link',
          name: 'Plaid Link',
          url: '/plaid-link/',
        },
        {
          id: 'consumer-report',
          name: 'Consumer Report',
          url: '/check/consumer-report/',
        },
        {
          id: 'layer',
          name: 'Layer',
          url: '/layer/',
        },
        {
          id: 'investments-move',
          name: 'Investments Move',
          url: '/products/investments-move/',
        },
        {
          id: 'protect',
          name: 'Protect',
          url: '/products/protect/',
        },
      ],
    },
    useCases: {
      header: 'Use Cases',
      items: [
        {
          id: 'banks-credit-unions',
          name: 'Banks & credit unions',
          url: '/industries/banks-credit-unions/',
        },
        {
          id: 'pay-by-bank',
          name: 'Pay by bank',
          url: '/pay-by-bank/',
        },
        {
          id: 'mortgage',
          name: 'Mortgage',
          url: '/industries/mortgage/',
        },
        {
          id: 'govt-public-sector',
          name: 'Government & public sector',
          url: '/industries/government-public-sector/',
        },
        {
          id: 'property-management',
          name: 'Property management',
          url: '/plaid-for-property-management/',
        },
        {
          id: 'gaming',
          name: 'Gaming',
          url: '/industries/gaming/',
        },
        {
          id: 'crypto',
          name: 'Crypto',
          url: '/use-cases/crypto/',
        },
        {
          id: 'lending',
          name: 'Lending',
          url: '/use-cases/lending/',
        },
        {
          id: 'business-finances',
          name: 'Business finances',
          url: '/use-cases/business-finances/',
        },
        {
          id: 'wealth',
          name: 'Wealth',
          url: '/use-cases/wealth/',
        },
      ],
    },
    docs: {
      header: 'Developers',
      items: [
        {
          id: 'quickstart',
          name: 'Quickstart',
          url: '/docs/quickstart/',
        },
        {
          id: 'api-documentation',
          name: 'API documentation',
          url: '/docs/',
        },
        {
          id: 'libraries',
          name: 'Libraries',
          url: '/docs/api/libraries/',
        },
        {
          id: 'github',
          name: 'GitHub',
          url: 'https://github.com/plaid',
        },
        {
          id: 'link-demo',
          name: 'Link demo',
          url: '/demo/',
        },
      ],
    },
    aboutUs: {
      header: 'About us',
      items: [
        {
          id: 'company',
          name: 'Company',
          url: '/company/',
        },
        {
          id: 'careers',
          name: 'Careers',
          url: '/careers/',
        },
        {
          id: 'contact',
          name: 'Contact',
          url: '/contact/',
        },
        {
          id: 'partners',
          name: 'Partners',
          url: '/partners/',
        },
        {
          id: 'press',
          name: 'Press',
          url: '/press/',
        },
        {
          id: 'safety',
          name: 'Safety',
          url: '/safety/',
        },
        {
          id: 'legal',
          name: 'Legal',
          url: '/legal/#privacy-statement',
        },
      ],
    },
    resources: {
      header: 'Resources',
      items: [
        {
          id: 'pricing',
          name: 'Pricing',
          url: '/pricing/',
          trackingId: NAVIGATION_EVENTS.FOOTER.USER_CLICKED_FOOTER_NAV_PRICING,
        },
        {
          id: 'global-coverage',
          name: 'Global coverage',
          url: '/global/',
        },
        {
          id: 'plaid-blog',
          name: 'Plaid Blog',
          url: '/blog/',
        },
        {
          id: 'industry-resources',
          name: 'Industry resources',
          url: '/resources/',
        },
        {
          id: 'annual-conference',
          name: 'Annual conference',
          url: '/events/effects/',
        },
        {
          id: 'customer-stories',
          name: 'Customer stories',
          url: '/customer-stories/',
        },
      ],
    },
    forConsumers: {
      header: 'For consumers',
      items: [
        {
          id: 'troubleshoot-connection',
          name: 'Troubleshoot account connection',
          url: '/trouble-connecting/',
        },
        {
          id: 'trust-safety',
          name: 'Trust and safety',
          url: '/trust-safety/',
        },
        {
          id: 'how-it-works-consumers',
          name: 'How it works',
          url: '/how-it-works-for-consumers/',
        },
        {
          id: 'plaid-portal',
          name: 'Plaid Portal',
          url: '//my.plaid.com',
        },
        {
          id: 'delete-data',
          name: 'Delete my data',
          url: '//my.plaid.com/data-subject-request-form',
        },
        {
          id: 'end-user-privacy-policy',
          name: 'End user privacy policy',
          url: '/legal/#end-user-privacy-policy',
        },
        {
          id: 'biometric-policy',
          name: 'Biometric Policy',
          url: '/legal/#biometric-policy-and-release',
        },
        {
          id: 'faqs',
          name: 'FAQs',
          url: '//my.plaid.com/help',
        },
        {
          id: 'consumer-reporting-agency',
          name: 'Plaid Consumer Reporting Agency, Inc.',
          url: '/check/consumer-service-center/',
        },
      ],
    },
    forInstitutions: {
      header: 'For financial institutions',
      items: [
        {
          id: 'open-finance-solution',
          name: 'Open Finance Solution',
          url: '/solutions/open-finance/',
        },
        {
          id: 'core-exchange',
          name: 'Core Exchange',
          url: '/products/core-exchange/',
        },
        {
          id: 'permissions-manager',
          name: 'Permissions Manager',
          url: '/products/permissions-manager/',
        },
        {
          id: 'app-directory',
          name: 'App Directory',
          url: '/products/app-directory/',
        },
      ],
    },
  },
};
